<template>
    <!-- PROCESS WORKFLOW -->
    <div class="workflow-container d-flex flex-column flex-fill" v-if="isStartedJob">
        <div class="row flex-grow-1">
            <div class="col">
                <div class="loading-container" :class="[ isWaitingJob ? 'd-flex' : 'd-none']">
                    <div class="loading small">
                        <img :src="loadingIcon" alt="">
                        <span class="font-weight-bold">{{ $t(`global.workflow.waiting`) }}</span>
                    </div>
                </div>

                <div class="panel-holder">
                    <div class="view-container">
                        <div id="divPlantsWithoutUrls" :class="[ isExecutingJob ? 'd-block' : 'd-none']">
                            <div class="innerDetails">
                                <div class="loading small mb-3">
                                    <img :src="loadingIcon" alt="">
                                    <span class="font-weight-bold">{{ currentProgressJob.msg }}</span>
                                </div>
                                <ul class="list-group list-group-flush">
                                    <li
                                        v-for="(progress, idx) in progressJob"
                                        :key="`progress-${idx}`"
                                        class="list-group-item px-0 text-muted small"
                                        :class="[progress.status=='ok' ? '' : 'txt-error']"
                                        >
                                        <span class="">
                                            <i class="fas" :class="[progress.status=='ok' ? 'fa-check' : 'fa-times']"></i>
                                        </span>
                                        <span v-html="progress.msg"></span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div id="divPlantsUrls" :class="[ isCompletedJob ? 'd-block' : 'd-none']">
                            <slot name="completedJob"
                                :dataJob="dataJob"
                                :isFailedJob="isFailedJob"
                                :titleList="titleList"
                                :description="description"
                                :emptyResult="emptyResult"
                                :errorResult="errorResult">

                                <div class="innerDetails"
                                    v-if="hasValidResult">
                                    <p v-html="resultDescription"></p>
                                    <ul class="group-plant-list list-unstyled">
                                        <li>
                                            <div v-if="hasIndividualDocsJob">
                                                <span>{{ $t(titleList) }}</span>
                                                <ul class="list-plant mb-3">
                                                    <li class="item-plant" v-for="(doc, idx) in individualDocsJob" :key="idx">
                                                        <a target="_blank" :href="doc.url">
                                                            {{ doc.label }}
                                                        </a>
                                                    </li>
                                                </ul>
                                            </div>
                                            <div class="uniqueDocsContainer" v-if="hasGlobalDocJob">
                                                <span>{{ $t(`global.workflow.result.global.title`) }}</span>
                                                <ul class="list-unique">
                                                    <li class="item-unique">
                                                        <a target="_blank" :href="globalDocJob.url">
                                                            {{ globalDocJob.label }}
                                                        </a>
                                                    </li>
                                                </ul>
                                            </div>
                                            <div class="uniqueReportContainer" v-if="hasReport">
                                                <p class="mb-2" v-if="titleList">{{ $t(titleList) }}</p>
                                                <slot name="report"
                                                    :data="reportJob">
                                                </slot>
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                                <div id="noOutput" v-if="!isValidResult">
                                    <template v-if="isFailedJob">
                                        <p class="txt-error">
                                            {{ $t(errorResult) }}
                                        </p>
                                    </template>
                                    <template v-else-if="hasErrors">
                                        <p>{{ $t(titleErrors) }}</p>
                                        <ul class="list-errors mt-3">
                                            <li class="item-error" v-for="(error, idx) in errorsJob" :key="idx">
                                                <span class="txt-error" v-html="error"></span>
                                            </li>
                                        </ul>
                                    </template>
                                    <template v-else>
                                        <div v-html="$t(emptyResult)"></div>
                                    </template>
                                </div>
                            </slot>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="btn-group responsive" role="group" v-if="isCompletedJob">
            <slot name="footer-prepend"></slot>
            <button
                v-if="showBtnClose"
                class="btn btn-main btn-rounded btn-sm btn-block"
                type="button"
                @click.prevent="close">
                {{ buttons['close'] }}
            </button>
            <slot name="footer-append"></slot>
        </div>
    </div>
</template>

<script>
import { jobStates } from './config/job-states';
import { isArray } from '@config/utils'
import DotsIcon from '@images/dots-loader.gif';

export default {
    props: {
        description: {
            type: String,
            default: 'global.workflow.result.description',
        },
        titleList: {
            type: String,
            default: 'global.workflow.result.docs.title',
        },
        emptyResult: {
            type: String,
            default: 'global.workflow.result.empty',
        },
        titleErrors: {
            type: String,
            default: 'global.workflow.result.error.title',
        },
        errorResult: {
            type: String,
            default: 'global.workflow.result.error.description',
        },
        url: {
            type: String,
            require: true,
        },
        id: {
            type: Number,
            default: null,
        },
        canceled: {
            type: Boolean,
            default: false,
        },
        showBtnClose: {
            type: Boolean,
            default: true,
        },
        titleBtnClose: {
            type: String,
            default: 'global.close',
        },
    },
    data() {
        return {
            job: {
                ms: 2 * 1000, //2s
                canceled: false,
                status: null,
                progress: null,
                data: null,
                docs: [],
                report: [],
                errors: [],
            },
            additional: [],
        }
    },
    computed: {
        loadingIcon() {
            return DotsIcon;
        },
        buttons() {
            return {
                'close': this.$t(this.titleBtnClose),
            };
        },
        isWaitingJob(){
            return this.job.status === jobStates.Waiting;
        },
        isExecutingJob(){
            return this.job.status === jobStates.Executing;
        },
        isRetryingJob(){
            return this.job.status === jobStates.Retrying;
        },
        isFinishedJob(){
            return this.job.status === jobStates.Finished;
        },
        isFailedJob(){
            return this.job.status === jobStates.Failed;
        },
        isCanceledJob(){
            return this.job.canceled;
        },
        isStartedJob(){
            return this.job.status!==null;
        },
        isCompletedJob(){
            return this.isFinishedJob || this.isFailedJob  || this.isCanceledJob;
        },
        currentProgressJob(){
            let defaultProgress = {msg: this.$t(`global.workflow.progress.msg`), 'status': 'ok'};
            return this.job.progress ? (isArray(this.job.progress) && this.job.progress.length ? this.job.progress[0] : defaultProgress) : defaultProgress;
        },
        progressJob(){
            return this.job && this.job.progress ? this.job.progress.filter( (item, idx) => idx>0) : [];
        },
        dataJob(){
            return this.job.data;
        },
        individualDocsJob(){
            return this.job?.docs?.individual || [];
        },
        hasIndividualDocsJob(){
            return this.individualDocsJob && this.individualDocsJob.length > 0;
        },
        globalDocJob(){
            return this.job?.docs?.global || null;
        },
        hasGlobalDocJob(){
            return !!this.globalDocJob;
        },
        reportJob(){
            return this.job.report || [];
        },
        errorsJob(){
            return this.job.errors ? this.job.errors : [];
        },
        hasErrors() {
            return this.errorsJob.length > 0;
        },
        hasDocs() {
            return this.hasIndividualDocsJob || this.hasGlobalDocJob;
        },
        hasReport() {
            return this.reportJob && this.reportJob.length > 0;
        },
        hasValidResult(){
            return this.hasDocs || this.hasReport;
        },
        isValidResult(){
            return !this.hasErrors && this.hasValidResult;
        },
        resultDescription() {
            let txt = [
                this.$t(this.description),
                this.hasDocs ? this.$t('global.workflow.result.docs.description') : ''
            ].filter(n => n).join('<br/>');
            return txt ?? '';
        },
    },
    mounted() {
        // if(this.id){
        //     this.waitingProcess();
        // }
    },
    async beforeDestroy() {
        await this.cancelJob();
        this.reset();
    },
    methods: {
        waitingProcess: function() {
            let ms = this.job.ms;

            const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

            // const waitingCallback = (job) => {
            //     console.log('waiting', job)
            // };

            // Initial state
            this.job.status = jobStates.Waiting;

            // Retrieve the initial operation status
            const getStatus = async (id) => {
                try {
                    const resp = await this.$http.get(this.url.replace(':id', id));

                    let job = resp.data?.data || null;
                    this.job.status = job.status;
                    this.job.progress = job.output && job.output.progress ? (isArray(job.output.progress) ? job.output.progress.reverse() : job.output.progress) : null;

                    this.additional = resp.data ? Object.fromEntries(Object.entries(resp.data).filter(([key]) => !key.includes('data'))) : [];

                    // Loop while the operation is still in progress...
                    if(!this.isCompletedJob) {
                        await delay(ms);
                        getStatus(id);
                    }else{
                        Object.keys(job.output)
                        .filter(key => !['progress', 'index', 'total'].includes(key))
                        .forEach(key => {
                            if(this.job.hasOwnProperty(key)){
                                this.job[key] = job.output[key];
                            }
                        });
                        // this.job.errors = job.output && job.output.errors ? job.output.errors : [];
                        // this.job.docs = job.output && job.output.docs ? job.output.docs : null;
                        // this.job.data = job.output && job.output.data ? job.output.data : null;
                    }
                } catch (error) {
                    this.$emit('onHttpError', error);
                    this.reset();
                }
            };
            getStatus(this.id);
        },
        async cancelJob(){
            await new Promise((resolve, reject) => {
                this.job.canceled = true;
                // wait
                setTimeout(() => resolve(), this.job.ms*1.1);
            });

        },
        resetJob: function(){
            Object.assign(this.$data.job, this.$options.data().job);
        },
        reset: function(){
            this.resetJob();
        },
        close: function(){
            this.$emit('close');
        },
    },
    watch: {
        id: {
            immediate: true,
            handler(value, oldVal) {
                if(value){
                    this.waitingProcess();
                }else{
                    this.reset();
                }
            },
        },
        isCompletedJob: {
            immediate: false,
            handler(value, oldVal) {
                if(value){
                    this.$emit('completed', {
                        data: this.job.data,
                        additional: this.additional,
                        error: !this.isValidResult,
                        errors: this.errorsJob,
                    });
                }
            },
        },
        canceled: {
            immediate: false,
            handler(value, oldVal) {
                if(value){
                    this.cancelJob();
                }
            },
        },
    },
};
</script>

<style lang="scss" scoped>
.title {
    margin-bottom: 1.5rem;
}
.loading > img {
    width: 60px;
}
</style>